<template>
    <div class="recording-audio" :class="{ 'recording-audio_full': buttonView }">
        <button
            @click="
                handleRecord({
                    audio: true
                })
            "
            :class="{ 'button-view': buttonView }"
            class="recording-audio-handler"
        >
            <LoaderIcon v-if="loading" />
            <RecordVoiceIcon v-else />

            <default-title class="ml-8" large :font-weight="400" v-if="buttonView">
                {{ t("course.practice.record_audio") }}
            </default-title>
        </button>

        <transition name="fade-up">
            <div v-if="error" class="recording-modal recording-modal_large">
                <default-title
                    v-html="error"
                    class="mb-10 mt-10"
                    :size="14"
                    :weight="400"
                    :text-color="'#52565C'"
                    :line-height="18"
                    font-family="Inter, sans-serif"
                >
                </default-title>

                <flex-container class="mt-10">
                    <DefaultButton
                        @click="cancelRecord()"
                        :height="40"
                        full-width
                        size="medium"
                        background="cancel"
                        :text="t('expert.cancel')"
                    />
                    <DefaultButton
                        @click="
                            handleRecord({
                                audio: true
                            })
                        "
                        :height="40"
                        full-width
                        size="medium"
                        background="green"
                        :text="t('quiz.try_again')"
                    />
                </flex-container>
            </div>
        </transition>

        <transition name="fade-up">
            <div v-if="isOpen" class="recording-modal">
                <flex-container align="center">
                    <div class="analyser-items" :style="{ gridTemplateColumns: `repeat(${num}, 5px)` }">
                        <div v-for="index of num" :key="index" class="analyser-item" />
                    </div>

                    <default-title
                        style="min-width: 40px"
                        :weight="400"
                        class="ml-10"
                        text-color="#323030"
                        font-family="Inter, sans-serif"
                    >
                        {{ formatTimer(time) }}
                    </default-title>
                </flex-container>

                <flex-container class="mt-10">
                    <DefaultButton
                        @click="cancelRecord()"
                        :height="40"
                        full-width
                        size="large"
                        background="cancel"
                        :text="t('expert.cancel')"
                    />
                    <DefaultButton
                        @click="stopRecord()"
                        class="ml-10"
                        :height="40"
                        full-width
                        size="large"
                        background="green"
                        :text="t('expert.add')"
                    />
                </flex-container>
            </div>
        </transition>
    </div>
</template>

<script>
import DefaultButton from "@components/Buttons/DefaultButton.vue"
import FlexContainer from "@components/Containers/FlexContainer.vue"
import LoaderIcon from "@components/Loaders/LoaderIcon.vue"
import DefaultTitle from "@components/Typography/DefaultTitle.vue"
import RecordVoiceIcon from "@icons/RecordVoiceIcon.vue"
import { createFileTask } from "@components/Files/FileTasks.vue"
import formatForFilename from "@mixins/formatDate"

export default {
    components: { LoaderIcon, FlexContainer, DefaultTitle, DefaultButton, RecordVoiceIcon },
    props: {
        buttonView: {
            type: Boolean,
            default: false
        }
    },
    name: "RecordingAudio",
    mixins: [formatForFilename],
    data() {
        return {
            recorder: null,
            canceled: false,
            stream: null,
            context: null,
            frequency: new Uint8Array(40),
            num: 40,
            time: 0,
            interval: null,
            isOpen: false,
            name: "",
            error: "",
            loading: false,
            requestAnimation: null,
            chunks: [],
            mimeType: "",
            recordingStartTime: 0,
            dataAvailableCallCount: 0,
            permissionState: "prompt" // 'granted', 'denied', 'prompt'
        }
    },
    // Constants
    RECORDING_START_DELAY: 100,
    TIMESLICE_INTERVAL: 1000,
    MIN_BLOB_SIZE: 100,
    TIMER_INTERVAL: 1000,
    methods: {
        logRecordingError(errorType, additionalContext = {}) {
            const baseContext = {
                mimeType: this.mimeType,
                recordingDuration: this.time,
                dataAvailableCalls: this.dataAvailableCallCount,
                chunksCount: this.chunks.length,
                // Environment context
                userAgent: navigator.userAgent,
                isSecureContext: window.isSecureContext, // Important for checks
                visibilityState: document.visibilityState, // Important for background tabs
                permissionState: this.permissionState
            }
            const fullContext = {...baseContext, ...additionalContext}

            console.error(`[RecordingAudio] ${errorType} - Full context:`, fullContext)

            return fullContext
        },
        /**
         * Debug logging helper - can be disabled in production
         * @param {string} level - log level (info, warn, error)
         * @param {string} message - log message
         * @param {any} data - additional data to log
         */
        log(level, message, ...data) {
            const prefix = "[RecordingAudio]"
            if (level === "error") {
                console.error(prefix, message, ...data)
            } else if (process.env.NODE_ENV === "development") {
                if (level === "warn") {
                    console.warn(prefix, message, ...data)
                } else {
                    console.log(prefix, message, ...data)
                }
            }
        },

        /**
         * Log recording error with context for Sentry
         * @param {string} errorType - Type of error
         * @param {object} additionalContext - Additional context data
         */
        logRecordingError(errorType, additionalContext = {}) {
            const baseContext = {
                mimeType: this.mimeType,
                recordingDuration: this.time,
                dataAvailableCalls: this.dataAvailableCallCount,
                chunksCount: this.chunks.length
            }
            const fullContext = { ...baseContext, ...additionalContext }

            console.error(`[RecordingAudio] ${errorType} - Full context:`, fullContext)

            return fullContext
        },
        /**
         * Detect if browser is Safari (including iOS Chrome which uses WebKit)
         * @returns {boolean}
         */
        isSafari() {
            const ua = navigator.userAgent.toLowerCase()
            // Safari usually has "safari" but not "chrome" (Desktop Chrome has both)
            // iOS Chrome has "crios" instead of "chrome", so it might fall into this check or not
            // depending on string matching, but iOS browsers generally share WebKit quirks.
            return ua.indexOf("safari") > -1 && ua.indexOf("chrome") === -1
        },
        /**
         * Detect browser-supported MIME type with priority fallback
         * @returns {string} Supported MIME type
         */
        detectSupportedMimeType() {
            // Determine a browser-supported format with priority fallback
            const mimeTypes = [
                "audio/webm;codecs=opus", // Chrome/Edge - best option
                "audio/webm", // Chrome/Edge fallback
                "audio/ogg;codecs=opus", // Firefox
                "audio/mp4", // Safari
                "audio/mpeg", // Rare case
                "" // Let the browser choose
            ]

            const supportedType = mimeTypes.find(type => {
                if (type === "") return true
                return MediaRecorder.isTypeSupported(type)
            })

            this.log("info", "Detected MIME type:", supportedType)

            return supportedType
        },
        async checkPermissionsState() {
            if (navigator.permissions && navigator.permissions.query) {
                try {
                    const permissionStatus = await navigator.permissions.query({name: "microphone"})
                    this.permissionState = permissionStatus.state

                    permissionStatus.onchange = () => {
                        this.permissionState = permissionStatus.state
                        this.log("info", "Permission state changed to:", this.permissionState)
                    }

                    return permissionStatus.state
                } catch (e) {
                    this.log("warn", "Permissions API not supported for microphone", e)
                    return "unknown"
                }
            }
            return "unsupported"
        },
        async handleRecord(constraints = {audio: true}) {
            // 1. Update permission state before trying
            await this.checkPermissionsState()

            // 2. Check Secure Context (HTTPS)
            if (!window.isSecureContext && window.location.hostname !== "localhost") {
                this.logRecordingError("Insecure Context", {
                    hostname: window.location.hostname,
                    protocol: window.location.protocol
                })
                this.error =
                    this.t("quiz.no_devices_access") +
                    "<br><br>" +
                    "Recording audio requires a secure connection (HTTPS)."
                return
            }

            // 3. Check MediaDevices API
            if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
                this.logRecordingError("API Unsupported", {
                    mediaDevices: !!navigator.mediaDevices,
                    getUserMedia: !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia)
                })
                this.error = this.t("quiz.no_devices_access")
                return
            }

            // iOS Safari Fix: AudioContext must be created/resumed synchronously on user gesture
            // to avoid NotAllowedError
            if (!this.context) {
                const AudioContext = window.AudioContext || window.webkitAudioContext
                this.context = new AudioContext()
            }

            if (this.context.state === "suspended") {
                this.context.resume()
            }

            this.canceled = false
            this.chunks = []

            this.log("info", "Requesting getUserMedia...", {permissionState: this.permissionState})

            navigator.mediaDevices.getUserMedia(constraints).then(this.onSuccess, this.onError)
        },
        onSuccess(stream) {
            this.permissionState = "granted" // If we are here, it is granted
            this.isOpen = true
            this.error = ""

            this.startTimer()

            this.mimeType = this.detectSupportedMimeType()

            const mediaRecorder = new MediaRecorder(stream, this.mimeType ? { mimeType: this.mimeType } : {})

            this.stream = stream
            this.recorder = mediaRecorder

            mediaRecorder.onstop = () => this.handleRecordingStop()
            mediaRecorder.ondataavailable = e => this.handleDataAvailable(e)

            // Small delay to stabilize audio stream before starting recording
            setTimeout(() => {
                if (this.recorder && this.recorder.state === "inactive") {
                    // Store recording start time to calculate actual duration for WebM duration fix
                    this.recordingStartTime = Date.now()

                    // Conditional fix:
                    // Safari (WebKit) produces empty chunks or fails to fire events if timeslice is used.
                    // Other browsers (Chrome/Firefox) benefit from timeslice to avoid file corruption.
                    if (this.isSafari()) {
                        mediaRecorder.start()
                    } else {
                        mediaRecorder.start(this.$options.TIMESLICE_INTERVAL)
                    }
                }
            }, this.$options.RECORDING_START_DELAY)

            this.visualizeFrequency()
        },

        startTimer() {
            if (this.interval) {
                clearInterval(this.interval)
                this.time = 0
            }

            this.time += this.$options.TIMER_INTERVAL

            this.interval = setInterval(() => {
                this.time += this.$options.TIMER_INTERVAL
            }, this.$options.TIMER_INTERVAL)
        },

        handleDataAvailable(event) {
            this.dataAvailableCallCount++ // Track how many times this is called

            if (event.data && event.data.size > 0) {
                this.log("info", `Data chunk received: ${event.data.size} bytes`)
                this.chunks.push(event.data)
            } else {
                this.log("warn", `ondataavailable called but no data, call #${this.dataAvailableCallCount}`)
            }
        },

        async handleRecordingStop() {
            this.log("info", "handleRecordingStop called, canceled:", this.canceled)

            // Stop timer immediately
            if (this.interval) {
                clearInterval(this.interval)
                this.interval = null
            }

            // Stop visualization animation
            if (this.requestAnimation) {
                cancelAnimationFrame(this.requestAnimation)
                this.requestAnimation = null
            }

            if (this.canceled) {
                // Clean up stream when canceled
                this.cleanupStream()
                return
            }

            // Wait a bit to ensure all data chunks are collected
            await new Promise(resolve => setTimeout(resolve, 100))

            // Check if we have valid chunks
            if (!this.chunks || this.chunks.length === 0) {
                this.logRecordingError("No audio data recorded", {
                    recorderState: this.recorder?.state,
                    mediaRecorderSupported: typeof MediaRecorder !== "undefined"
                })
                this.error = this.t("quiz.no_devices_access")
                this.isOpen = false
                this.cleanupStream()
                return
            }

            const totalSize = this.chunks.reduce((sum, c) => sum + c.size, 0)
            this.log("info", `Chunks collected: ${this.chunks.length}, Total size: ${totalSize} bytes`)

            try {
                // Use the MIME type that was determined when creating MediaRecorder
                let blob = new Blob(this.chunks, { type: this.mimeType })

                this.log("info", `Blob created: ${blob.size} bytes, type: ${blob.type}`)

                // Check blob size
                if (blob.size < this.$options.MIN_BLOB_SIZE) {
                    this.logRecordingError("Audio blob too small", {
                        blobSize: blob.size,
                        minSize: this.$options.MIN_BLOB_SIZE
                    })
                    this.error = "Recording failed. Please try again."
                    this.isOpen = false
                    this.cleanupStream()
                    return
                }

                // Clean up stream before saving
                this.cleanupStream()

                // Save directly without conversion
                this.handleBlob(blob)
            } catch (e) {
                this.logRecordingError("Blob creation error", {
                    errorMessage: e.message,
                    errorStack: e.stack
                })
                this.error = "Recording failed. Please try again."
                this.isOpen = false
                this.cleanupStream()
            }
        },
        onError(error) {
            // Detailed error analysis
            const errorName = error?.name
            const errorMessage = error?.message

            this.logRecordingError("getUserMedia failed", {
                errorName: errorName,
                errorMessage: errorMessage,
                constraint: error?.constraint,
                permissionState: this.permissionState // Was it already denied?
            })

            // User-friendly error mapping
            if (errorName === "NotAllowedError" || errorName === "PermissionDeniedError") {
                // Distinction: If permissionState was ALREADY 'denied', the user blocked it previously.
                // If it was 'prompt'/'granted' but failed now, it might be the Browser Policy (Safari issue) or user clicked 'Block' just now.

                if (this.permissionState === "denied") {
                    this.error =
                        "Microphone access is blocked. Please allow access in your browser settings (lock icon in address bar)."
                } else {
                    // Fallback for when user clicks "Block" right now OR Safari blocks it due to context
                    this.error = "Access denied. Please allow microphone access when prompted."
                }
            } else if (errorName === "NotFoundError" || errorName === "DevicesNotFoundError") {
                this.error = "No microphone found. Please connect a microphone and try again."
            } else if (errorName === "NotReadableError" || errorName === "TrackStartError") {
                this.error = "Microphone is busy or used by another application."
            } else if (errorName === "OverconstrainedError") {
                this.error = "Microphone does not satisfy the requirements."
            } else {
                // Generic fallback
                this.error = this.t("quiz.no_devices_access")
            }
        },

        getFileExtensionFromMimeType(mimeType) {
            // Map MIME types to file extensions
            const mimeToExt = {
                "audio/webm": "webm",
                "audio/ogg": "ogg",
                "audio/mp4": "m4a",
                "audio/mpeg": "mp3",
                "audio/wav": "wav"
            }

            // Find exact match or by prefix (for types with codecs)
            for (const [mime, ext] of Object.entries(mimeToExt)) {
                if (mimeType.startsWith(mime)) {
                    return ext
                }
            }

            // Fallback to webm
            return "webm"
        },

        handleBlob(blob) {
            this.isOpen = false

            if (!this.formatForFilename) {
                console.error("[RecordingAudio] formatForFilename method is not available, using fallback")
            }

            const timestamp = this.formatForFilename ? this.formatForFilename(new Date()) : Date.now()
            const extension = this.getFileExtensionFromMimeType(blob.type)
            const name = `audio-recording-${timestamp}.${extension}`

            this.log("info", `Saving file: ${name}, MIME: ${blob.type}, Size: ${blob.size} bytes`)

            const file = new File([blob], name, { type: blob.type })

            this.name = name

            this.$nextTick(() => {
                const formData = new FormData()

                formData.append("file", blob, name)

                if (window.current_cabinet_id) {
                    formData.append("cabinet_id", window.current_cabinet_id)
                }

                this.name = name

                this.loading = true

                file.meta = {
                    model: "comment"
                }

                createFileTask([file])
                    .then(result => {
                        for (const resultElement of result) {
                            if (resultElement.status === "fulfilled") {
                                this.$emit("save", resultElement.value)
                            } else if (resultElement.status === "rejected") {
                                console.error("[RecordingAudio] File upload failed:", {
                                    fileName: name,
                                    errorMessage: resultElement.reason?.message,
                                    errorStack: resultElement.reason?.stack
                                })
                            }
                        }
                    })
                    .catch(error => {
                        console.error("[RecordingAudio] createFileTask failed:", {
                            fileName: name,
                            errorMessage: error?.message,
                            errorStack: error?.stack
                        })
                    })
                    .finally(() => {
                        this.cancelRecord()
                        this.loading = false
                    })
            })
        },
        visualizeFrequency() {
            if (!this.context) {
                const AudioContext = window.AudioContext || window.webkitAudioContext
                this.context = new AudioContext()
            }

            if (!this.stream) {
                return
            }

            const source = this.context.createMediaStreamSource(this.stream)

            const analyser = this.context.createAnalyser()

            source.connect(analyser)

            this.animate(analyser)
        },
        animate(analyser) {
            const requestAnimationFrame =
                window.requestAnimationFrame ||
                window.mozRequestAnimationFrame ||
                window.webkitRequestAnimationFrame ||
                window.msRequestAnimationFrame

            this.requestAnimation = requestAnimationFrame(() => this.animate(analyser))
            analyser.getByteFrequencyData(this.frequency)

            const analyserElements = this.$el.querySelectorAll(".analyser-item")
            for (let i = 0; i < analyserElements.length; i++) {
                const height = this.frequency[i] / 8
                analyserElements[i].style.height = height + "px"
            }
        },
        formatTimer(t) {
            return new Date(t).toISOString().slice(14, 19)
        },
        /**
         * Clean up media stream and stop all tracks
         */
        cleanupStream() {
            if (this.stream) {
                this.log("info", "Cleaning up stream tracks")
                this.stream.getTracks().forEach(track => {
                    track.stop()
                    this.log("info", `Track stopped: ${track.kind}`)
                })
                this.stream = null
            }
        },

        cancelRecord() {
            this.log("info", "cancelRecord called")
            this.canceled = true

            if (this.recorder && this.recorder.state !== "inactive") {
                try {
                    this.recorder.stop()
                } catch (e) {
                    this.logRecordingError("Recorder stop error on cancel", {
                        errorMessage: e.message,
                        errorStack: e.stack,
                        recorderState: this.recorder?.state
                    })
                }
            }

            // Stream cleanup will be done in handleRecordingStop
            // to avoid race conditions

            // Stop visualization animation
            if (this.requestAnimation) {
                cancelAnimationFrame(this.requestAnimation)
                this.requestAnimation = null
            }

            // Clear timer
            if (this.interval) {
                clearInterval(this.interval)
                this.interval = null
            }

            this.isOpen = false
            this.time = 0
            this.error = ""
            this.chunks = []
            this.dataAvailableCallCount = 0
        },

        stopRecord() {
            this.log("info", "stopRecord called, recorder state:", this.recorder?.state)

            if (this.recorder && this.recorder.state === "recording") {
                try {
                    // Request data one more time before stopping to ensure all data is captured
                    // Only needed when using timeslice (non-Safari)
                    if (!this.isSafari()) {
                        this.recorder.requestData()
                    }

                    // Stop recording - this will trigger onstop event
                    this.recorder.stop()

                    this.log("info", "Recorder.stop() called successfully")
                } catch (e) {
                    this.logRecordingError("Error stopping recorder", {
                        errorMessage: e.message,
                        errorStack: e.stack,
                        recorderState: this.recorder?.state
                    })
                    this.error = "Recording failed. Please try again."
                    this.cancelRecord()
                }
            } else {
                this.log("warn", "Recorder is not in recording state:", this.recorder?.state)
            }
        }
    }
}
</script>

<style scoped lang="sass">
.recording-audio
    position: relative
    max-width: 460px
.recording-modal
    position: absolute
    min-width: 270px
    right: -50px
    top: 0
    background-color: #fff
    z-index: 999999
    border-radius: 10px
    box-shadow: 0 4px 10px rgba(128, 158, 191, 0.15)
    padding: 14px 12px
    &_large
        min-width: 320px
        &::v-deep
            .default-button
                padding: 0 12px !important
.analyser-items
    min-height: 40px
    display: grid
    align-items: center
    .analyser-item
        background-color: #3965FF
        width: 2px
        min-height: 3px
.recording-audio-handler
    width: 30px
    height: 30px
    display: flex
    align-items: center
    justify-content: center
    border-radius: 4px
    background-color: #fff
    transition: .2s
    z-index: 2
    @media screen and (max-width: 640px)
        width: 24px
        height: 24px
    &:hover
        background-color: #F2F7FF
    &::v-deep
        path
            stroke: #3E4755
    &.button-view
        width: max-content
.progress-display
    width: 100%
    background-color: #F3F3F3
    border-radius: 100px
    height: 5px
    transition: .2s
</style>
